import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { EASE, Reveal, CountUp, ImageReveal } from './motion/primitives';

function slugify(label) {
  return label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
}

/* Masked line-by-line reveal for HTML titles containing <br/> */
function TitleReveal({ html, className, baseDelay = 0.2 }) {
  const reduce = useReducedMotion();
  const lines = html.split(/<br\s*\/?>/i);
  return (
    <h1 className={className}>
      {lines.map((line, i) => (
        <span key={i} style={{ display: 'block', overflow: 'hidden', paddingBottom: '0.1em', marginBottom: '-0.1em' }}>
          <motion.span
            style={{ display: 'block', willChange: 'transform' }}
            initial={reduce ? false : { y: '112%', rotate: 2 }}
            animate={{ y: '0%', rotate: 0 }}
            transition={{ duration: 0.9, delay: baseDelay + i * 0.09, ease: EASE }}
            dangerouslySetInnerHTML={{ __html: line }}
          />
        </span>
      ))}
    </h1>
  );
}

function HeroImage({ heroImage, heroPlaceholder }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['-6%', '6%']);

  return (
    <motion.div
      ref={ref}
      className="cs-hero-image"
      style={heroImage ? { background: 'none', padding: 0 } : {}}
      initial={reduce ? false : { opacity: 0, clipPath: 'inset(4% 3% 4% 3% round 32px)' }}
      whileInView={{ opacity: 1, clipPath: 'inset(0% 0% 0% 0% round 0px)' }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 1.1, ease: EASE }}
    >
      {heroImage ? (
        <motion.img
          src={heroImage}
          alt=""
          initial={reduce ? false : { scale: 1.12 }}
          whileInView={{ scale: 1.08 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 1.4, ease: EASE }}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: 0, y: reduce ? 0 : y }}
        />
      ) : (
        <>
          <div className="placeholder-icon">
            <svg width="24" height="24" fill="none" stroke="#A8C898" strokeWidth="1.5" viewBox="0 0 24 24">
              <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/>
            </svg>
          </div>
          <p className="placeholder-text">{heroPlaceholder}</p>
        </>
      )}
    </motion.div>
  );
}

export default function CaseStudy({ data }) {
  const {
    company, title, subtitle,
    metaRole, metaPlatform, metaExtra, metaExtraLabel,
    outcomeValue, outcomeSub,
    heroPlaceholder,
    heroImage,
    metaItems,
    sections,
    nextTo, nextLabel, nextTitle,
  } = data;

  const tocItems = sections.filter(s => s.type === 'text' && s.label);
  const [activeSlug, setActiveSlug] = useState(tocItems[0] ? slugify(tocItems[0].label) : '');
  const [scrollPct, setScrollPct] = useState(0);
  const contentRef = useRef(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    const onScroll = () => {
      const el = contentRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = el.offsetHeight;
      const scrolled = Math.max(0, -rect.top);
      setScrollPct(Math.min(100, (scrolled / total) * 100));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const ids = tocItems.map(s => slugify(s.label));
    const observers = [];

    ids.forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveSlug(id); },
        { rootMargin: '-20% 0px -70% 0px' }
      );
      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach(o => o.disconnect());
  }, [sections]); // eslint-disable-line

  const scrollTo = (slug) => {
    const el = document.getElementById(slug);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -100, duration: 1.2 });
    else el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <div className="header-blob-wrap">
        <div className="header-blob" />
      </div>
      <header className="cs-header">
        <div className="cs-header-left">
          <TitleReveal html={title} className="cs-title" baseDelay={0.28} />
          <motion.p
            className="cs-subtitle"
            initial={{ opacity: 0, y: 22, filter: 'blur(6px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.7, delay: 0.6, ease: EASE }}
          >
            {subtitle}
          </motion.p>
        </div>
        <div className="cs-header-right">
          <motion.div
            className="cs-meta-mini"
            initial={{ opacity: 0, x: 44 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: EASE }}
          >
            <div>
              <span className="cs-meta-mini-label">My Role</span>
              <span className="cs-meta-mini-value">{metaRole}</span>
            </div>
            <div>
              <span className="cs-meta-mini-label">Platform</span>
              <span className="cs-meta-mini-value">{metaPlatform}</span>
            </div>
            <div>
              <span className="cs-meta-mini-label">{metaExtraLabel}</span>
              <span className="cs-meta-mini-value">{metaExtra}</span>
            </div>
          </motion.div>
          <motion.div
            className="cs-outcome-card"
            initial={{ opacity: 0, x: 44 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
            whileHover={reduce ? undefined : { scale: 1.02, transition: { duration: 0.3 } }}
          >
            <span className="cs-outcome-label">Key Outcome</span>
            <span className="cs-outcome-value"><CountUp value={outcomeValue} delay={0.9} /></span>
            <span className="cs-outcome-sub">{outcomeSub}</span>
          </motion.div>
        </div>
      </header>

      <HeroImage heroImage={heroImage} heroPlaceholder={heroPlaceholder} />

      <div className="cs-meta">
        {metaItems.map((item, i) => (
          <motion.div
            className="meta-item"
            key={i}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6, delay: i * 0.1, ease: EASE }}
          >
            <p className="meta-label">{item.label}</p>
            <p className="meta-value">{item.value}</p>
          </motion.div>
        ))}
      </div>

      <div className="cs-body">
        {/* TOC Sidebar */}
        <aside className="cs-toc">
          <div className="toc-progress-track">
            <div className="toc-progress-bar" style={{ height: `${scrollPct}%` }} />
          </div>
          <nav className="toc-nav">
            {tocItems.map((s) => {
              const slug = slugify(s.label);
              return (
                <button
                  key={slug}
                  className={`toc-item${activeSlug === slug ? ' toc-active' : ''}`}
                  onClick={() => scrollTo(slug)}
                >
                  {s.label}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main content */}
        <div className="cs-content" ref={contentRef}>
          {sections.map((section, i) => (
            <div key={i}>
              {section.type === 'text' && (
                <div className="cs-section" id={slugify(section.label)}>
                  <Reveal y={20} blur={false} duration={0.6}>
                    <p className="cs-section-label">{section.label}</p>
                  </Reveal>
                  <Reveal y={26} delay={0.08}>
                    <h2>{section.heading}</h2>
                  </Reveal>
                  {section.paragraphs.map((p, j) => (
                    <Reveal key={j} y={24} delay={0.05 * j} amount={0.3}>
                      <p dangerouslySetInnerHTML={{ __html: p }} />
                    </Reveal>
                  ))}
                  {section.hmw && (
                    <motion.div
                      className="hmw"
                      initial={reduce ? false : { opacity: 0, scale: 0.96, rotate: -0.6 }}
                      whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                      viewport={{ once: true, amount: 0.4 }}
                      transition={{ duration: 0.7, ease: EASE }}
                    >
                      <p>{section.hmw}</p>
                    </motion.div>
                  )}
                  {section.highlight && (
                    <motion.div
                      className="highlight-box"
                      initial={reduce ? false : { opacity: 0, x: -28 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, amount: 0.4 }}
                      transition={{ duration: 0.7, ease: EASE }}
                    >
                      <p dangerouslySetInnerHTML={{ __html: section.highlight }} />
                    </motion.div>
                  )}
                  {section.impact && (
                    <motion.div
                      className="impact-grid"
                      initial={reduce ? false : { opacity: 0, y: 32 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.3 }}
                      transition={{ duration: 0.7, ease: EASE }}
                    >
                      {section.impact.map((item, k) => (
                        <motion.div
                          className="impact-item"
                          key={k}
                          initial={reduce ? false : { opacity: 0, y: 24 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true, amount: 0.3 }}
                          transition={{ duration: 0.6, delay: 0.15 + k * 0.12, ease: EASE }}
                        >
                          <p className="impact-num"><CountUp value={item.num} delay={0.2 + k * 0.12} /></p>
                          <p className="impact-label">{item.label}</p>
                        </motion.div>
                      ))}
                    </motion.div>
                  )}
                  {section.afterImpact && section.afterImpact.map((p, j) => (
                    <Reveal key={j} y={24} amount={0.3}>
                      <p dangerouslySetInnerHTML={{ __html: p }} />
                    </Reveal>
                  ))}
                </div>
              )}
              {section.type === 'image' && (
                <>
                  {section.src ? (
                    <ImageReveal
                      src={section.src}
                      alt={section.caption}
                      style={{ width: '100%', margin: '32px 0 12px' }}
                      imgStyle={{ height: 'auto' }}
                    />
                  ) : (
                    <div className={`image-placeholder ${section.size}`}>
                      <div className="placeholder-icon">
                        <svg width="20" height="20" fill="none" stroke="#A8C898" strokeWidth="1.5" viewBox="0 0 24 24">
                          <rect x="5" y="2" width="14" height="20" rx="2"/><path d="M12 18h.01"/>
                        </svg>
                      </div>
                      <p className="placeholder-text">{section.text}</p>
                    </div>
                  )}
                  <Reveal y={10} blur={false} duration={0.5} amount={0.6}>
                    <span className="image-caption">{section.caption}</span>
                  </Reveal>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <Reveal y={36} amount={0.3}>
        <Link to={nextTo} className="next-project">
          <div>
            <p className="next-label">{nextLabel}</p>
            <h3 className="next-title">{nextTitle}</h3>
          </div>
          <div className="next-arrow">
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M7 17L17 7M17 7H7M17 7v10"/>
            </svg>
          </div>
        </Link>
      </Reveal>

      <footer>
        <span className="footer-copy">© Updated March 2026 · Muneeb Khan</span>
        <span className="footer-made">Designed & Built by Muneeb</span>
      </footer>
    </>
  );
}
